import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api'

export interface Employee {
  id: string
  tenant_id: string
  user_id?: string | null
  employee_number: string
  code_1c?: string | null
  iin: string
  first_name: string
  last_name: string
  middle_name?: string | null
  nickname?: string | null
  email?: string | null
  phone?: string | null
  department_id?: string | null
  position_id?: string | null
  warehouse_id?: string | null
  hire_date: string
  fire_date?: string | null
  probation_end_date?: string | null
  contract_number?: string | null
  contract_type?: string | null
  photo_url?: string | null
  is_active: boolean
  is_materially_responsible: boolean
  created_at: string
  department?: {
    id: string
    name: string
  } | null
  position?: {
    id: string
    name: string
  } | null
  warehouse?: {
    id: string
    name: string
    code_1c: string
  } | null
  user?: {
    id: string
    email: string
    role_id?: string | null
    role?: {
      id: string
      name: string
    } | null
  } | null
  invitation?: {
    id: string
    invite_code: string
    expires_at: Date | string
    is_valid: boolean
  } | null
  schedule_template?: {
    id: string
    name: string
  } | null
}

export interface EmployeeFilters {
  search?: string
  departmentId?: string
  positionId?: string
  managerId?: string
  isActive?: boolean
  page?: number
  limit?: number
  sort?: string
}

export interface PaginatedEmployees {
  data: Employee[]
  total: number
  page: number
  limit: number
  totalPages: number
  activeCount: number
  from1cCount: number
}

const EMPLOYEES_QUERY_KEY = 'employees'

export const useEmployees = (filters: EmployeeFilters = {}) => {
  return useQuery({
    queryKey: [EMPLOYEES_QUERY_KEY, filters],
    queryFn: async (): Promise<PaginatedEmployees> => {
      const response = await apiClient.getEmployees(filters)
      // Приводим ответ API к нужному формату
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
        totalPages: response.totalPages || 1,
        activeCount: response.activeCount || 0,
        from1cCount: response.from1cCount || 0,
      }
    },
    staleTime: 30 * 1000, // 30 seconds
    retry: 3,
  })
}

export const useEmployee = (id: string) => {
  return useQuery({
    queryKey: [EMPLOYEES_QUERY_KEY, id],
    queryFn: async (): Promise<Employee> => {
      const response = await apiClient.getEmployee(id)
      return response.data
    },
    enabled: !!id,
    staleTime: 30 * 1000,
  })
}

export const useCreateEmployee = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: Partial<Employee>) => {
      console.log('[useCreateEmployee] Sending data:', data)
      const response = await apiClient.createEmployee(data)
      console.log('[useCreateEmployee] Received response:', response)
      console.log('[useCreateEmployee] Response has invitation?', !!response?.invitation)
      // Backend returns Employee object directly, not wrapped in {data: ...}
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EMPLOYEES_QUERY_KEY] })
    },
  })
}

export const useUpdateEmployee = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Employee> }) => {
      console.log('[useUpdateEmployee] Sending to API:', { id, data })
      const response = await apiClient.updateEmployee(id, data)
      console.log('[useUpdateEmployee] API response:', response)
      return response.data
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [EMPLOYEES_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [EMPLOYEES_QUERY_KEY, id] })
    },
  })
}

export const useDeleteEmployee = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.deleteEmployee(id)
      // Для DELETE операций response может быть null (код 204)
      return response?.data || response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EMPLOYEES_QUERY_KEY] })
    },
  })
}